import { useEffect, useRef, useState } from "react";
import Header from "../../components/Header";
import {
    Text,
    View,
    Pressable,
    StyleSheet,
    useWindowDimensions,
} from "react-native";
import { useAuthUser } from "../../contexts/useAuthUser";
import { firestore } from "../../services/firebase/index";
import { ActivityIndicator, Provider } from "react-native-paper";
import { Container } from './style';
import { useNavigation } from "@react-navigation/native";
import {
    getDocs,
    query,
    doc,
    documentId,
    collection,
    collectionGroup,
    getDoc,
    getCountFromServer,
    orderBy,
    limit,
    startAfter,
    writeBatch,
    where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { AlertBox } from "../../components/AlertBox";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { useRemoteConfigContext } from "../../contexts/useRemoteConfigContext";
import { Feather } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import { Badge } from "../AdminPanel/Products/formStyles";
import {
    isLegacyCommunityPost,
    VIRTUAL_GENERAL_CHANNEL_ID,
    VIRTUAL_GENERAL_CHANNEL_NAME,
} from "../../utils/communityChannelUtils";

const PAGE_SIZE = 20;
const STATUSES = ['unread', 'read'];

// Caixa de entrada mantida pelos triggers (functions/services/commentInbox.js):
// um doc por comentário em commentInbox/{catalog|community}/{unread|read},
// com a data em `sortAt` (ms). Cada uma é coleção comum: paginar por data e
// contar por categoria/canal usam só índices automáticos. Antes a tela lia
// todos os comentários e todos os posts a cada abertura.
const inboxCollection = (source, status) => collection(firestore, 'commentInbox', source, status);

const countOf = async (q) => (await getCountFromServer(q)).data().count;

// A tela lê a caixa por esta interface: `page` (20 por vez, mais novos
// primeiro), `count` da aba e `countBy` (soma de não lidos + lidos com
// `field == value`). `markRead`/`remove` acompanham o que a tela gravou.
const firestoreInbox = {
    count: (source, status) => countOf(inboxCollection(source, status)),
    countBy: async (source, field, value) => {
        const counts = await Promise.all(
            STATUSES.map((s) => countOf(query(inboxCollection(source, s), where(field, '==', value))))
        );
        return counts[0] + counts[1];
    },
    page: async (source, status, cursor) => {
        const snap = await getDocs(query(
            inboxCollection(source, status),
            orderBy('sortAt', 'desc'),
            ...(cursor ? [startAfter(cursor)] : []),
            // Um a mais só para saber se existe próxima página.
            limit(PAGE_SIZE + 1),
        ));
        const docs = snap.docs.slice(0, PAGE_SIZE);
        return {
            rows: docs.map((d) => ({ id: d.id, ref: d.ref, entry: d.data() })),
            cursor: docs[docs.length - 1] ?? cursor,
            hasMore: snap.docs.length > PAGE_SIZE,
        };
    },
    markRead: () => {},
    remove: () => {},
};

// ---- Sem as functions no projeto ----
// O Deploy em Massa publica Web e Functions em paralelo (e dá para publicar
// só o Web); o localhost também costuma apontar para um projeto de cliente.
// Sem a callable, a tela não pode parar: lê os comentários direto, como
// antes, e monta a mesma caixa em memória. Origem, status e data seguem as
// regras de functions/services/commentInbox.js.

const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (value instanceof Date) return value.getTime() || 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return Date.parse(value) || 0;
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    return 0;
};

const postChannelId = (postData) =>
    postData && !isLegacyCommunityPost(postData) ? String(postData.channelId) : null;

const toMemoryEntry = (path, data) => {
    const parts = path.split('/');
    let ids = null;
    if (parts.length === 8 && parts[0] === 'categories' && parts[2] === 'cards' && parts[4] === 'videos' && parts[6] === 'comments') {
        ids = { categoryId: parts[1], cardId: parts[3], videoId: parts[5] };
    } else if (parts.length === 4 && parts[0] === 'posts' && parts[2] === 'comments') {
        ids = { postId: parts[1] };
    }
    if (!ids) return null;
    return {
        id: parts.join('|'),
        source: ids.postId ? 'community' : 'catalog',
        status: data.approved ? 'read' : 'unread',
        entry: { ...data, ...ids, path, sortAt: toMillis(data.createdAt) },
    };
};

const loadMemoryInbox = async () => {
    const snap = await getDocs(collectionGroup(firestore, 'comments'));
    const items = snap.docs.map((d) => toMemoryEntry(d.ref.path, d.data())).filter(Boolean);

    // Canal de cada comentário de post, lendo só os posts que têm comentário.
    const channelByPost = new Map();
    await fillCacheByIds(
        channelByPost,
        'posts',
        items.filter((it) => it.source === 'community').map((it) => it.entry.postId),
        postChannelId
    );
    items.forEach((it) => {
        if (it.source === 'community') it.entry.channelId = channelByPost.get(it.entry.postId) ?? null;
    });

    const lists = {};
    items.forEach((it) => {
        const key = `${it.source}/${it.status}`;
        if (!lists[key]) lists[key] = [];
        lists[key].push(it);
    });
    Object.values(lists).forEach((list) => list.sort((a, b) => b.entry.sortAt - a.entry.sortAt));

    // O que sai de uma lista é só marcado (`gone`): o cursor é a posição na
    // lista, e tirar o item deslocaria a próxima página.
    const list = (source, status) => lists[`${source}/${status}`] || [];
    const alive = (source, status) => list(source, status).filter((it) => !it.gone);
    const find = (source, status, id) => list(source, status).find((it) => it.id === id && !it.gone);

    return {
        count: async (source, status) => alive(source, status).length,
        countBy: async (source, field, value) =>
            STATUSES.reduce((n, s) => n + alive(source, s).filter((it) => it.entry[field] === value).length, 0),
        page: async (source, status, cursor) => {
            const start = cursor ?? 0;
            const all = list(source, status);
            return {
                rows: all.slice(start, start + PAGE_SIZE).filter((it) => !it.gone).map((it) => ({ id: it.id, ref: null, entry: it.entry })),
                cursor: start + PAGE_SIZE,
                hasMore: start + PAGE_SIZE < all.length,
            };
        },
        markRead: (source, id) => {
            const it = find(source, 'unread', id);
            if (!it) return;
            it.gone = true;
            const read = list(source, 'read');
            read.push({ ...it, gone: false, status: 'read' });
            read.sort((a, b) => b.entry.sortAt - a.entry.sortAt);
            lists[`${source}/read`] = read;
        },
        remove: (source, status, id) => {
            const it = find(source, status, id);
            if (it) it.gone = true;
        },
    };
};

// Executa `worker` sobre os itens com um teto de simultaneidade — mas SEMPRE
// em paralelo até esse teto. Serializar aqui foi o que deixou a tela mais
// lenta numa tentativa anterior: agrupar as leituras reduz o número de
// requisições, mas só ajuda se elas continuarem saindo ao mesmo tempo.
const mapWithLimit = async (items, limitCount, worker) => {
    const results = new Array(items.length);
    let cursor = 0;

    const runners = Array.from({ length: Math.min(limitCount, items.length) }, async () => {
        while (cursor < items.length) {
            const index = cursor++;
            results[index] = await worker(items[index]);
        }
    });

    await Promise.all(runners);
    return results;
};

// Lê em blocos de 30 (`documentId() in [...]`, sem exigir índice) os docs de
// `collectionName` que ainda não estão em `cache`, guardando `pick(data)`.
// Quem não voltou (doc apagado) fica com null.
const fillCacheByIds = async (cache, collectionName, ids, pick) => {
    const missing = [...new Set(ids.filter((id) => id && !cache.has(id)))];
    const chunks = [];
    for (let i = 0; i < missing.length; i += 30) chunks.push(missing.slice(i, i + 30));

    await mapWithLimit(chunks, 12, async (chunk) => {
        try {
            const snap = await getDocs(
                query(collection(firestore, collectionName), where(documentId(), 'in', chunk))
            );
            snap.forEach((d) => cache.set(d.id, pick(d.data())));
        } catch (error) {
            console.error(`Erro ao buscar ${collectionName}:`, error);
        }
    });
    missing.forEach((id) => { if (!cache.has(id)) cache.set(id, null); });
};

// Top 5 categorias por nº de comentários: uma contagem no servidor por
// categoria, em vez de ler todos os comentários.
const fetchCatalogStats = async (store) => {
    const categoriesSnap = await getDocs(collection(firestore, 'categories'));
    const rows = await mapWithLimit(categoriesSnap.docs, 10, async (d) => ({
        name: d.data()?.title || 'Sem categoria',
        count: await store.countBy('catalog', 'categoryId', d.id),
    }));
    return {
        topCategories: rows
            .filter((r) => r.count > 0)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5),
    };
};

// Posts e comentários por canal, contados no servidor. O "Geral" (posts sem
// canal) é o total menos a soma dos canais existentes.
const fetchCommunityStats = async (store, totalComments) => {
    const postsRef = collection(firestore, 'posts');
    const [channelsSnap, totalPosts] = await Promise.all([
        getDocs(collection(firestore, 'communityChannels')),
        countOf(postsRef),
    ]);
    const channels = await mapWithLimit(channelsSnap.docs, 10, async (d) => {
        const [posts, comments] = await Promise.all([
            countOf(query(postsRef, where('channelId', '==', d.id))),
            store.countBy('community', 'channelId', d.id),
        ]);
        return { id: d.id, name: d.data()?.name || 'Canal', posts, comments };
    });

    const sum = (key) => channels.reduce((acc, ch) => acc + ch[key], 0);
    const general = {
        id: VIRTUAL_GENERAL_CHANNEL_ID,
        name: VIRTUAL_GENERAL_CHANNEL_NAME,
        posts: Math.max(0, totalPosts - sum('posts')),
        comments: Math.max(0, totalComments - sum('comments')),
    };
    return {
        // Sempre mostra o Geral e os canais existentes (mesmo sem atividade, para
        // permitir a comparação).
        channels: [general, ...channels].sort(
            (a, b) => (b.posts + b.comments) - (a.posts + a.comments)
        ),
        realChannelCount: channelsSnap.size,
    };
};

// Formata a data do comentário com segurança (Timestamp do Firestore, Date ou ausente).
const formatCommentDate = (createdAt) => {
    try {
        const d = createdAt?.toDate
            ? createdAt.toDate()
            : createdAt instanceof Date
                ? createdAt
                : null;
        return d ? d.toLocaleString('pt-BR') : '';
    } catch {
        return '';
    }
};

export function Comments() {
    const { customInfo } = useCustomTheme();
    const navigation = useNavigation();
    const { width } = useWindowDimensions();
    const isMobile = width < 768;

    const [activeTab2, setActiveTab2] = useState('catalog'); //divisão entre comentários da comunidade ou do catálogo
    const [activeTab, setActiveTab] = useState('unread'); //divisão entre comentários não lidos ('unread') e aprovados ('read')

    // A comunidade é exclusiva do plano Premium. Esta tela mostrava a aba
    // "Comunidade" para todo mundo, sem checar nada — um cliente do plano
    // Growth via o painel de engajamento de um recurso que não contratou.
    // Exige as duas condições: o plano (a regra comercial) e a flag, que o
    // admin pode desligar mesmo sendo Premium.
    const { hasCommunity } = useRemoteConfigContext();
    const canUseCommunity = hasCommunity && customInfo?.planType === 'premium';
    // Valor DERIVADO, não estado: enquanto o plano e a flag carregam, a origem
    // cai em "catalog" sozinha, sem uma janela em que a aba da comunidade fique
    // ativa e sem depender de um efeito para corrigir depois.
    const commentSource = canUseCommunity ? activeTab2 : 'catalog';
    const viewKey = `${commentSource}/${activeTab}`;

    // Páginas já carregadas por origem/status ({ items, cursor, hasMore }) e,
    // por origem, as contagens das abas e o painel de análise. Trocar de aba
    // e voltar não relê nada; "Atualizar" descarta tudo.
    const [views, setViews] = useState({});
    const [sources, setSources] = useState({});
    const [refreshKey, setRefreshKey] = useState(0);
    const [loadError, setLoadError] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // Uma página de "Carregar mais" que volta depois do "Atualizar" pertence à
    // lista velha e é descartada.
    const generationRef = useRef(0);
    // Nomes de alunos, títulos de aula e textos de post já resolvidos: a
    // próxima página e a outra aba não leem de novo.
    const namesRef = useRef(new Map());
    const videoTitlesRef = useRef(new Map());
    const postTextsRef = useRef(new Map());
    const storeRef = useRef(null);

    const { user, authUser } = useAuthUser();
    // `pendingDelete` guarda o comentário aguardando confirmação de exclusão.
    const [showAlert, setShowAlert] = useState({
        visibleAlert: false,
        title: null,
        message: null,
        pendingDelete: null,
    });

    const view = views[viewKey];
    const sourceData = sources[commentSource];
    const loading = !!authUser && !loadError && (!view || !sourceData);

    // A caixa é preenchida uma vez por projeto, na primeira abertura depois
    // do deploy: a function copia os comentários que já existiam (e os
    // triggers a mantêm dali em diante). Nas demais, é só a leitura da marca.
    // Sem a function no projeto, cai na caixa em memória.
    const openInbox = async () => {
        try {
            const state = await getDoc(doc(firestore, 'commentInbox', 'state'));
            if (!state.exists()) {
                const backfill = httpsCallable(getFunctions(), 'backfillCommentInbox', { timeout: 540000 });
                await backfill();
            }
            return firestoreInbox;
        } catch (error) {
            console.warn('Caixa de comentários indisponível (functions não publicadas?); lendo os comentários direto.', error);
            return loadMemoryInbox();
        }
    };

    const getStore = () => {
        if (!storeRef.current) {
            storeRef.current = openInbox().catch((error) => {
                storeRef.current = null;
                throw error;
            });
        }
        return storeRef.current;
    };

    // Completa as linhas da página com o nome do aluno e o título da aula ou
    // do post — só das 20 da página, e só do que ainda não está em cache.
    const resolveRows = async (source, status, rows) => {
        const isCommunity = source === 'community';
        // O web grava o nome do autor no próprio comentário (`authorName` no
        // catálogo, `userName` na comunidade); só os demais (app mobile,
        // comentários antigos) custam a leitura do doc do aluno.
        const storedName = (entry) => entry.authorName || entry.userName || null;
        const entries = rows.map((r) => r.entry);
        const videoKey = (e) => `${e.categoryId}/${e.cardId}/${e.videoId}`;

        await Promise.all([
            fillCacheByIds(
                namesRef.current,
                'users',
                entries.filter((e) => !storedName(e)).map((e) => e.userId),
                (u) => u?.Nome_Completo || u?.Email || null
            ),
            isCommunity
                ? fillCacheByIds(
                    postTextsRef.current,
                    'posts',
                    entries.map((e) => e.postId),
                    (p) => p?.contentText || null
                )
                // Títulos de aula moram em subcoleções (um doc por aula): leituras
                // individuais, mas só das aulas distintas da página.
                : mapWithLimit(
                    [...new Set(entries.map(videoKey))].filter((key) => !videoTitlesRef.current.has(key)),
                    20,
                    async (key) => {
                        const [categoryId, cardId, videoId] = key.split('/');
                        try {
                            const snap = await getDoc(
                                doc(firestore, 'categories', categoryId, 'cards', cardId, 'videos', videoId)
                            );
                            videoTitlesRef.current.set(key, snap.data()?.title || null);
                        } catch {
                            videoTitlesRef.current.set(key, null);
                        }
                    }
                ),
        ]);

        return rows.map(({ id, ref, entry }) => {
            return {
                ...entry,
                id,
                // null na caixa em memória (sem as functions).
                entryRef: ref,
                // Dado cru da caixa, para regravar ao mover de lista.
                entry,
                isCommunity,
                approved: status === 'read',
                userName: storedName(entry) || namesRef.current.get(entry.userId) || 'Aluno sem nome',
                pageTitle: isCommunity
                    ? (postTextsRef.current.get(entry.postId) || 'Post da Comunidade')
                    : (videoTitlesRef.current.get(videoKey(entry)) || 'Aula'),
            };
        });
    };

    const fetchPage = async (source, status, cursor) => {
        const store = await getStore();
        const page = await store.page(source, status, cursor);
        return {
            items: await resolveRows(source, status, page.rows),
            cursor: page.cursor,
            hasMore: page.hasMore,
        };
    };

    const fetchSourceInfo = async (source) => {
        const store = await getStore();
        const [unread, read] = await Promise.all(STATUSES.map((s) => store.count(source, s)));
        const stats = source === 'community'
            ? await fetchCommunityStats(store, unread + read)
            : await fetchCatalogStats(store);
        return { counts: { unread, read }, stats };
    };

    // Carrega o que falta da aba aberta: a primeira página e, na primeira vez
    // da origem, as contagens e o painel de análise.
    useEffect(() => {
        if (!authUser) return undefined;
        const source = commentSource;
        const status = activeTab;
        const needView = !views[viewKey];
        const needSource = !sources[source];
        if (!needView && !needSource) return undefined;

        let cancelled = false;
        setLoadError(false);
        (async () => {
            try {
                const [page, info] = await Promise.all([
                    needView ? fetchPage(source, status, null) : null,
                    needSource ? fetchSourceInfo(source) : null,
                ]);
                if (cancelled) return;
                if (page) setViews((prev) => ({ ...prev, [`${source}/${status}`]: page }));
                if (info) setSources((prev) => ({ ...prev, [source]: info }));
            } catch (error) {
                console.error('Erro ao carregar comentários:', error);
                if (!cancelled) setLoadError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [authUser, viewKey, refreshKey]);

    // A tela fica montada no menu e carrega uma vez só: sem isto, comentário
    // novo só aparecia recarregando a página inteira.
    const refresh = () => {
        generationRef.current += 1;
        // Relê a marca da caixa: as functions podem ter sido publicadas.
        storeRef.current = null;
        namesRef.current = new Map();
        videoTitlesRef.current = new Map();
        postTextsRef.current = new Map();
        setViews({});
        setSources({});
        setLoadError(false);
        setRefreshKey((k) => k + 1);
    };

    const loadMore = async () => {
        if (!view?.hasMore || loadingMore) return;
        const key = viewKey;
        const generation = generationRef.current;
        setLoadingMore(true);
        try {
            const page = await fetchPage(commentSource, activeTab, view.cursor);
            if (generation !== generationRef.current) return;
            setViews((prev) => {
                const current = prev[key];
                if (!current) return prev;
                const seen = new Set(current.items.map((c) => c.id));
                return {
                    ...prev,
                    [key]: {
                        items: [...current.items, ...page.items.filter((c) => !seen.has(c.id))],
                        cursor: page.cursor,
                        hasMore: page.hasMore,
                    },
                };
            });
        } catch (error) {
            console.error('Erro ao carregar mais comentários:', error);
        } finally {
            setLoadingMore(false);
        }
    };

    const sourceOf = (comment) => (comment.isCommunity ? 'community' : 'catalog');

    // Tira o comentário da lista carregada e acerta as contagens das abas.
    const removeFromView = (comment, status, countDelta) => {
        const source = sourceOf(comment);
        const key = `${source}/${status}`;
        setViews((prev) => {
            const next = { ...prev };
            if (prev[key]) {
                next[key] = { ...prev[key], items: prev[key].items.filter((c) => c.id !== comment.id) };
            }
            // A lista de lidos é por data: o aprovado entra na posição certa
            // quando ela for aberta de novo.
            if (countDelta.read > 0) delete next[`${source}/read`];
            return next;
        });
        setSources((prev) => {
            const current = prev[source];
            if (!current) return prev;
            const counts = { ...current.counts };
            Object.entries(countDelta).forEach(([s, delta]) => {
                counts[s] = Math.max(0, counts[s] + delta);
            });
            return { ...prev, [source]: { ...current, counts } };
        });
    };

    // A tela move a entrada da caixa na hora (o trigger repete, sem
    // diferença): contagens e listas já saem certas num "Atualizar" logo em
    // seguida, e uma entrada sobrando também se corrige por aqui.
    const toggleApproveComment = async (comment) => {
        const source = sourceOf(comment);
        const batch = writeBatch(firestore);
        batch.update(doc(firestore, comment.path), { approved: true, read: true });
        if (comment.entryRef) {
            batch.delete(comment.entryRef);
            batch.set(doc(inboxCollection(source, 'read'), comment.id), comment.entry);
        }
        try {
            const store = await getStore();
            try {
                await batch.commit();
            } catch (error) {
                if (error?.code !== 'not-found') throw error;
                // O comentário já foi apagado: só a entrada sobrou.
                if (comment.entryRef) await writeBatch(firestore).delete(comment.entryRef).commit();
                store.remove(source, 'unread', comment.id);
                removeFromView(comment, 'unread', { unread: -1 });
                return;
            }
            store.markRead(source, comment.id);
            removeFromView(comment, 'unread', { unread: -1, read: 1 });
        } catch (error) {
            console.error('Erro ao marcar comentário como lido:', error);
        }
    };

    const deleteComment = async (comment) => {
        try {
            const store = await getStore();
            const batch = writeBatch(firestore);
            batch.delete(doc(firestore, comment.path));
            if (comment.entryRef) batch.delete(comment.entryRef);
            await batch.commit();

            const status = comment.approved ? 'read' : 'unread';
            store.remove(sourceOf(comment), status, comment.id);
            removeFromView(comment, status, { [status]: -1 });
        } catch (error) {
            console.error("Erro ao excluir comentário:", error);
        }
    };

    const requestDeleteComment = (comment) => {
        setShowAlert({
            visibleAlert: true,
            title: 'Excluir comentário?',
            message: 'Esta ação é permanente e não pode ser desfeita.',
            pendingDelete: comment,
        });
    };

    const hideAlert = () => {
        setShowAlert({
            visibleAlert: false,
            title: null,
            message: null,
            pendingDelete: null,
        });
    };

    // Abre o conteúdo na MESMA aba, navegando dentro do app — que já está
    // logado. (Aba nova já deu "você não possui acesso": a sessão do Firebase é
    // por domínio, e o link ia para o `webUrl`, não para onde o admin estava.)
    //
    // `fromComments` faz o "Voltar" da aula/post retornar a esta lista; sem
    // ele, a aula volta para o módulo e o post, para o feed. A tela fica
    // montada no menu, então a volta não recarrega os comentários.
    const openCommentLink = (comment) => {
        if (comment.isCommunity) {
            navigation.navigate('CommunityComments', { id: comment.postId, fromComments: true });
            return;
        }
        navigation.navigate('Aula', {
            categoryId: comment.categoryId,
            cardId: comment.cardId,
            selectedVideo: comment.videoId,
            fromComments: true,
        });
    };

    const visibleComments = view?.items ?? [];
    const catalogStats = sourceData?.stats?.topCategories ?? [];
    const communityStats = sourceData?.stats?.channels ?? [];

    // "Mais de um canal" = existe ao menos um canal real além do "Geral" virtual.
    const hasMultipleChannels = (sourceData?.stats?.realChannelCount ?? 0) >= 1;

    // Pílula de aba (segmented control) reaproveitada para origem e status.
    const Segment = ({ active, onPress, label, count }) => (
        <Pressable
            onPress={onPress}
            style={({ hovered }) => [
                styles.segment,
                active && styles.segmentActive,
                hovered && !active && { backgroundColor: ADMIN_COLORS.surface },
            ]}
        >
            <Text style={[styles.segmentText, active && styles.segmentTextActive]}>{label}</Text>
            {typeof count === 'number' && (
                <View style={[styles.countPill, active && styles.countPillActive]}>
                    <Text style={[styles.countText, active && styles.countTextActive]}>{count}</Text>
                </View>
            )}
        </Pressable>
    );

    return (
        <Provider>
            <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
                <Header navigation={navigation} user={user} isAdminPage />

                <Container>
                    <View style={styles.page}>
                        {/* Cabeçalho da página */}
                        <View style={styles.headerBlock}>
                            <View style={{ flex: 1, minWidth: 0 }}>
                                <Text style={[styles.title, isMobile && { fontSize: 24 }]}>Comentários</Text>
                                <Text style={styles.subtitle}>
                                    {canUseCommunity
                                        ? 'Modere os comentários do catálogo e da comunidade.'
                                        : 'Modere os comentários do catálogo.'}
                                </Text>
                            </View>
                            {/* A tela fica montada no menu e carrega uma vez só:
                                sem isto, comentário novo só aparecia recarregando
                                a página inteira. */}
                            <Pressable
                                onPress={refresh}
                                disabled={loading}
                                style={({ hovered }) => [
                                    styles.actionBtn,
                                    styles.deleteBtn,
                                    hovered && !loading && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                    loading && { opacity: 0.5 },
                                ]}
                            >
                                <Feather name="refresh-cw" size={14} color={ADMIN_COLORS.ink} />
                                <Text style={[styles.actionText, { color: ADMIN_COLORS.ink }]}>Atualizar</Text>
                            </Pressable>
                        </View>

                        {/* Barra de filtros: origem + status. Fica na tela
                            enquanto a aba escolhida carrega. */}
                        <View style={styles.toolbar}>
                            {/* Sem comunidade, a origem é sempre o catálogo: o par
                                de botões vira um rótulo sem escolha, então some. */}
                            {canUseCommunity && (
                                <View style={styles.segmentGroup}>
                                    <Segment
                                        active={commentSource === 'catalog'}
                                        onPress={() => setActiveTab2('catalog')}
                                        label="Catálogo"
                                    />
                                    <Segment
                                        active={commentSource === 'community'}
                                        onPress={() => setActiveTab2('community')}
                                        label="Comunidade"
                                    />
                                </View>
                            )}

                            {/* `marginLeft: auto` mantém os filtros de status à
                                direita: o `space-between` da toolbar só alinha
                                assim quando existem dois grupos, e sem a
                                comunidade este fica sozinho. */}
                            <View style={[styles.segmentGroup, { marginLeft: 'auto' }]}>
                                <Segment
                                    active={activeTab === 'unread'}
                                    onPress={() => setActiveTab('unread')}
                                    label="Não lidos"
                                    count={sourceData?.counts.unread}
                                />
                                <Segment
                                    active={activeTab === 'read'}
                                    onPress={() => setActiveTab('read')}
                                    label="Lidos"
                                    count={sourceData?.counts.read}
                                />
                            </View>
                        </View>

                        {loadError ? (
                            <View style={styles.emptyState}>
                                <Feather name="alert-circle" size={28} color={ADMIN_COLORS.placeholder} />
                                <Text style={styles.emptyTitle}>Não foi possível carregar os comentários</Text>
                                <Text style={styles.emptySub}>
                                    Tente de novo em alguns minutos pelo botão "Atualizar".
                                </Text>
                            </View>
                        ) : loading ? (
                            <View style={styles.loadingBox}>
                                <ActivityIndicator color={ADMIN_COLORS.teal} size="large" />
                            </View>
                        ) : (
                          <>
                        {/* Painel de análise conforme a origem selecionada */}
                        {commentSource === 'catalog' ? (
                            <View style={styles.analyticsCard}>
                                <Text style={styles.analyticsTitle}>Categorias com mais comentários</Text>
                                <Text style={styles.analyticsSub}>
                                    Top 5 categorias do catálogo por volume de comentários.
                                </Text>
                                {catalogStats.length === 0 ? (
                                    <Text style={styles.analyticsEmpty}>Ainda não há comentários no catálogo.</Text>
                                ) : (
                                    <View style={{ marginTop: 8 }}>
                                        {catalogStats.map((row, i) => (
                                            <View
                                                key={`${row.name}-${i}`}
                                                style={[styles.rankRow, i > 0 && styles.rankRowDivider]}
                                            >
                                                <View style={styles.rankIndex}>
                                                    <Text style={styles.rankIndexText}>{i + 1}</Text>
                                                </View>
                                                <Text style={styles.rankName} numberOfLines={1}>{row.name}</Text>
                                                <View style={styles.rankCountPill}>
                                                    <Text style={styles.rankCountText}>{row.count}</Text>
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                )}
                            </View>
                        ) : (
                            <View style={styles.analyticsCard}>
                                <Text style={styles.analyticsTitle}>
                                    {hasMultipleChannels ? 'Engajamento por canal' : 'Engajamento da comunidade'}
                                </Text>
                                <Text style={styles.analyticsSub}>
                                    {hasMultipleChannels
                                        ? 'Posts e comentários em cada canal da comunidade.'
                                        : 'Posts e comentários do canal Geral.'}
                                </Text>
                                {communityStats.length === 0 ? (
                                    <Text style={styles.analyticsEmpty}>Ainda não há atividade na comunidade.</Text>
                                ) : (
                                    <View style={styles.channelGrid}>
                                        {communityStats.map((ch) => (
                                            <View key={ch.id} style={styles.channelCard}>
                                                <Text style={styles.channelName} numberOfLines={1}>{ch.name}</Text>
                                                <View style={styles.channelMetrics}>
                                                    <View style={styles.metric}>
                                                        <Text style={styles.metricValue}>{ch.posts}</Text>
                                                        <Text style={styles.metricLabel}>Posts</Text>
                                                    </View>
                                                    <View style={styles.metric}>
                                                        <Text style={styles.metricValue}>{ch.comments}</Text>
                                                        <Text style={styles.metricLabel}>Comentários</Text>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                )}
                            </View>
                        )}

                        {/* Lista de comentários */}
                        <View style={styles.list}>
                            {visibleComments.map((comment) => {
                                const initial = (comment.userName || '?').trim().charAt(0).toUpperCase();
                                return (
                                    <View key={comment.id} style={styles.card}>
                                        <View style={styles.cardTop}>
                                            <View style={styles.avatar}>
                                                <Text style={styles.avatarText}>{initial}</Text>
                                            </View>
                                            <View style={{ flex: 1, minWidth: 0 }}>
                                                <Text style={styles.author} numberOfLines={1}>
                                                    {comment.userName}
                                                </Text>
                                                <Text style={styles.date}>{formatCommentDate(comment.createdAt)}</Text>
                                            </View>
                                            <View style={styles.badges}>
                                                <Badge variant={comment.approved ? 'success' : 'info'}>
                                                    {comment.approved ? 'Lido' : 'Não lido'}
                                                </Badge>
                                            </View>
                                        </View>

                                        <Text style={styles.context}>
                                            Comentou em{' '}
                                            <Text
                                                style={styles.contextLink}
                                                onPress={() => openCommentLink(comment)}
                                                numberOfLines={3}
                                                ellipsizeMode="tail"
                                            >
                                                {comment.pageTitle}
                                            </Text>
                                        </Text>

                                        <Text style={styles.commentText}>{comment.comment}</Text>

                                        <View style={styles.cardActions}>
                                            {!comment.approved && (
                                                <Pressable
                                                    onPress={() => toggleApproveComment(comment)}
                                                    style={({ hovered }) => [
                                                        styles.actionBtn,
                                                        styles.approveBtn,
                                                        hovered && { backgroundColor: '#D1FAE5' },
                                                    ]}
                                                >
                                                    <Feather name="check" size={14} color={ADMIN_COLORS.success} />
                                                    <Text style={[styles.actionText, { color: ADMIN_COLORS.success }]}>
                                                        Marcar como lido
                                                    </Text>
                                                </Pressable>
                                            )}

                                            <Pressable
                                                onPress={() => requestDeleteComment(comment)}
                                                style={({ hovered }) => [
                                                    styles.actionBtn,
                                                    styles.deleteBtn,
                                                    hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
                                                ]}
                                            >
                                                <Feather name="trash-2" size={14} color={ADMIN_COLORS.danger} />
                                                <Text style={[styles.actionText, { color: ADMIN_COLORS.danger }]}>
                                                    Excluir
                                                </Text>
                                            </Pressable>
                                        </View>
                                    </View>
                                );
                            })}

                            {visibleComments.length === 0 && (
                                <View style={styles.emptyState}>
                                    <Feather name="message-square" size={28} color={ADMIN_COLORS.placeholder} />
                                    <Text style={styles.emptyTitle}>
                                        Nenhum comentário {activeTab === 'unread' ? 'não lido' : 'lido'}
                                    </Text>
                                    <Text style={styles.emptySub}>
                                        Os comentários {commentSource === 'catalog' ? 'do catálogo' : 'da comunidade'} aparecerão aqui.
                                    </Text>
                                </View>
                            )}

                            {view?.hasMore && (
                                <Pressable
                                    onPress={loadMore}
                                    disabled={loadingMore}
                                    style={({ hovered }) => [
                                        styles.actionBtn,
                                        styles.deleteBtn,
                                        styles.loadMoreBtn,
                                        hovered && !loadingMore && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                        loadingMore && { opacity: 0.6 },
                                    ]}
                                >
                                    {loadingMore
                                        ? <ActivityIndicator color={ADMIN_COLORS.teal} size={14} />
                                        : <Feather name="chevron-down" size={14} color={ADMIN_COLORS.ink} />}
                                    <Text style={[styles.actionText, { color: ADMIN_COLORS.ink }]}>
                                        {loadingMore ? 'Carregando...' : 'Carregar mais'}
                                    </Text>
                                </Pressable>
                            )}
                        </View>
                          </>
                        )}
                    </View>
                </Container>

                <AlertBox variant="admin"
                    title={showAlert.title}
                    message={showAlert.message}
                    visible={showAlert.visibleAlert}
                    onClose={hideAlert}
                    leftButton={showAlert.pendingDelete ? 'Cancelar' : 'OK'}
                    leftButtonFunction={hideAlert}
                    rightButton={showAlert.pendingDelete ? 'Excluir' : undefined}
                    rightButtonFunction={() => {
                        if (showAlert.pendingDelete) deleteComment(showAlert.pendingDelete);
                    }}
                />
            </View>
        </Provider>
    );
}

const styles = StyleSheet.create({
    page: {
        width: '100%',
        maxWidth: 960,
        alignSelf: 'center',
    },
    headerBlock: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        marginBottom: 20,
    },
    title: {
        fontFamily: ADMIN_FONT,
        fontSize: 28,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    subtitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        color: ADMIN_COLORS.muted,
        marginTop: 4,
    },
    toolbar: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
    },
    segmentGroup: {
        flexDirection: 'row',
        backgroundColor: ADMIN_COLORS.secondaryBg,
        borderRadius: 10,
        padding: 4,
        gap: 4,
    },
    segment: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
        paddingHorizontal: 14,
        borderRadius: 8,
        cursor: 'pointer',
    },
    segmentActive: {
        backgroundColor: ADMIN_COLORS.surface,
        boxShadow: '0 1px 2px rgba(16,24,40,0.08)',
    },
    segmentText: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '600',
        color: ADMIN_COLORS.muted,
    },
    segmentTextActive: {
        color: ADMIN_COLORS.ink,
    },
    countPill: {
        minWidth: 20,
        paddingHorizontal: 6,
        paddingVertical: 1,
        borderRadius: 999,
        backgroundColor: ADMIN_COLORS.border,
        alignItems: 'center',
        justifyContent: 'center',
    },
    countPillActive: {
        backgroundColor: ADMIN_COLORS.accentSoft,
    },
    countText: {
        fontFamily: ADMIN_FONT,
        fontSize: 11,
        fontWeight: '700',
        color: ADMIN_COLORS.muted,
    },
    countTextActive: {
        color: ADMIN_COLORS.accentDark,
    },
    list: {
        gap: 14,
    },
    loadingBox: {
        paddingVertical: 80,
        alignItems: 'center',
        justifyContent: 'center',
    },
    analyticsCard: {
        backgroundColor: ADMIN_COLORS.surface,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: ADMIN_COLORS.radius,
        padding: 16,
        marginBottom: 20,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    },
    analyticsTitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 15,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    analyticsSub: {
        fontFamily: ADMIN_FONT,
        fontSize: 12.5,
        color: ADMIN_COLORS.muted,
        marginTop: 2,
    },
    analyticsEmpty: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        marginTop: 12,
    },
    // Ranking de categorias (catálogo)
    rankRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 11,
    },
    rankRowDivider: {
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
    },
    rankIndex: {
        width: 24,
        height: 24,
        borderRadius: 999,
        backgroundColor: ADMIN_COLORS.accentSoft,
        alignItems: 'center',
        justifyContent: 'center',
    },
    rankIndexText: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        fontWeight: '700',
        color: ADMIN_COLORS.accentDark,
    },
    rankName: {
        fontFamily: ADMIN_FONT,
        fontSize: 13.5,
        fontWeight: '600',
        color: ADMIN_COLORS.ink,
        flex: 1,
        minWidth: 0,
    },
    rankCountPill: {
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 999,
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
    },
    rankCountText: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    // Engajamento por canal (comunidade)
    channelGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 12,
    },
    channelCard: {
        flexGrow: 1,
        flexBasis: 180,
        minWidth: 150,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 10,
        padding: 12,
        backgroundColor: ADMIN_COLORS.surfaceSubtle,
    },
    channelName: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
        marginBottom: 8,
    },
    channelMetrics: {
        flexDirection: 'row',
        gap: 20,
    },
    metric: {},
    metricValue: {
        fontFamily: ADMIN_FONT,
        fontSize: 20,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    metricLabel: {
        fontFamily: ADMIN_FONT,
        fontSize: 11,
        color: ADMIN_COLORS.muted,
        marginTop: 1,
    },
    card: {
        backgroundColor: ADMIN_COLORS.surface,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: ADMIN_COLORS.radius,
        padding: 16,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    },
    cardTop: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
    },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: ADMIN_COLORS.tealSoft,
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarText: {
        fontFamily: ADMIN_FONT,
        fontSize: 16,
        fontWeight: '700',
        color: ADMIN_COLORS.teal,
    },
    author: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: '700',
        color: ADMIN_COLORS.ink,
    },
    date: {
        fontFamily: ADMIN_FONT,
        fontSize: 12,
        color: ADMIN_COLORS.muted,
        marginTop: 2,
    },
    badges: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 6,
    },
    context: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        marginBottom: 8,
    },
    contextLink: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '600',
        color: ADMIN_COLORS.accentDark,
        textDecorationLine: 'underline',
    },
    commentText: {
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        lineHeight: 20,
        color: ADMIN_COLORS.label,
    },
    cardActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 14,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: ADMIN_COLORS.border,
    },
    actionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 7,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        cursor: 'pointer',
    },
    approveBtn: {
        backgroundColor: ADMIN_COLORS.successSoft,
        borderColor: ADMIN_COLORS.successBorder,
    },
    deleteBtn: {
        backgroundColor: ADMIN_COLORS.surface,
        borderColor: ADMIN_COLORS.border,
    },
    actionText: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        fontWeight: '600',
    },
    loadMoreBtn: {
        alignSelf: 'center',
        paddingHorizontal: 20,
        marginTop: 4,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
        gap: 8,
    },
    emptyTitle: {
        fontFamily: ADMIN_FONT,
        fontSize: 15,
        fontWeight: '600',
        color: ADMIN_COLORS.ink,
        marginTop: 4,
    },
    emptySub: {
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
        textAlign: 'center',
    },
});
